import { arenaSchema, type Arena } from '@growup/arena-shared';
import { Button, Container, cx, Logo, moduleNav, Notice, Select, Theme } from '@growup/ui';
import { useCallback, useEffect, useState } from 'react';
import { z } from 'zod';

import {
  acceptSession,
  getJson,
  getToken,
  messageOf,
  onTokenChange,
  refreshSession,
  signOut,
} from './api';
import { ArenaConfigView } from './arena/ArenaConfigView';
import { ArenasView } from './arena/ArenasView';
import { ContactsSection } from './contacts/ContactsSection';
import { ContentSection } from './content/ContentSection';
import { EventsSection } from './events/EventsSection';
import { GiveawaysSection } from './giveaways/GiveawaysSection';
import { TournamentsSection } from './tournaments/TournamentsSection';
import { RoomView } from './arena/RoomView';
import { OccupancyView } from './arena/OccupancyView';
import { SessionsView } from './arena/SessionsView';
import { YouthsView } from './arena/YouthsView';
import { MembersSection } from './members/MembersSection';
import {
  canManageContacts,
  canReadDirectionContacts,
  hasContactsAccess,
  canManageContent,
  canManagePartners,
  canManageEvents,
  canManageEventBudget,
  canManageGiveaways,
  canManageTournaments,
  canManageStaff,
  canManageStore,
  canManageSocial,
  canManageStreaming,
  canProgramChannel,
  hasStreamingAccess,
  isStreamingPadrinho,
  canRegister,
  canViewMembers,
  decodeSession,
  hasArenaAccess,
  hasMembersAccess,
  hasSocialAccess,
  hasTeamsAccess,
  isAdmin,
  type StaffSession,
} from './session';
import { SignIn } from './SignIn';
import { SocialSection } from './social/SocialSection';
import { StaffView } from './staff/StaffView';
import { StoreSection } from './store/StoreSection';
import { StreamingSection } from './streaming/StreamingSection';
import { TeamsSection } from './teams/TeamsSection';

type Section =
  | 'arena'
  | 'socios'
  | 'loja'
  | 'eventos'
  | 'equipas'
  | 'torneios'
  | 'passatempos'
  | 'conteudos'
  | 'redes'
  | 'streaming'
  | 'contactos'
  | 'staff';

const SECTIONS: { id: Section; label: string; visible: (s: StaffSession) => boolean }[] = [
  { id: 'arena', label: 'Arena', visible: hasArenaAccess },
  { id: 'socios', label: 'Sócios', visible: hasMembersAccess },
  { id: 'loja', label: 'Loja', visible: canManageStore },
  { id: 'eventos', label: 'Eventos', visible: canManageEvents },
  { id: 'equipas', label: 'Equipas', visible: hasTeamsAccess },
  { id: 'torneios', label: 'Torneios', visible: canManageTournaments },
  { id: 'passatempos', label: 'Passatempos', visible: canManageGiveaways },
  {
    id: 'conteudos',
    label: 'Conteúdos',
    visible: (s) => canManageContent(s) || canManagePartners(s),
  },
  { id: 'redes', label: 'Redes sociais', visible: hasSocialAccess },
  { id: 'streaming', label: 'Streaming', visible: hasStreamingAccess },
  { id: 'contactos', label: 'Contactos', visible: hasContactsAccess },
  { id: 'staff', label: 'Staff', visible: canManageStaff },
];

type Tab = 'sala' | 'jovens' | 'sessoes' | 'ocupacao' | 'config' | 'arenas';

const TABS: { id: Tab; label: string; adminOnly?: boolean }[] = [
  { id: 'sala', label: 'Sala' },
  { id: 'jovens', label: 'Jovens' },
  { id: 'sessoes', label: 'Sessões' },
  { id: 'ocupacao', label: 'Ocupação' },
  { id: 'config', label: 'Configuração', adminOnly: true },
  { id: 'arenas', label: 'Arenas', adminOnly: true },
];

export function App() {
  const [token, setTokenState] = useState<string | null>(getToken);
  // On load, a refresh cookie from an earlier visit may still be valid.
  const [restoring, setRestoring] = useState(true);

  useEffect(() => onTokenChange(setTokenState), []);
  useEffect(() => {
    void refreshSession().finally(() => setRestoring(false));
  }, []);

  const session = token ? decodeSession(token) : null;

  if (restoring && !session)
    return (
      <Theme name="admin" className="min-h-screen">
        {null}
      </Theme>
    );
  if (!session) return <SignIn onSignedIn={acceptSession} />;
  const sections = SECTIONS.filter((s) => s.visible(session));
  if (sections.length === 0) {
    return (
      <Theme
        name="admin"
        className="flex min-h-screen flex-col items-center justify-center gap-3 px-4"
      >
        <p>A conta {session.login} não tem nenhum papel no back-office.</p>
        <Button variant="ghost" size="sm" onClick={() => void signOut()}>
          Sair
        </Button>
      </Theme>
    );
  }
  return <Backoffice session={session} sections={sections} />;
}

/** Os sites publicos, noutra aba: o back-office nao entra no seletor dos sites. */
function SiteLinks() {
  return (
    <nav aria-label="Sites Grow uP" className="hidden items-center gap-3 md:flex">
      <span className="text-caption text-gu-ink-muted">Ver</span>
      {moduleNav('site').map((m) => (
        <a
          key={m.href}
          href={m.href}
          target="_blank"
          rel="noopener noreferrer"
          className="font-sub text-label font-bold uppercase text-gu-ink-muted hover:text-gu-ink"
        >
          {m.label}
        </a>
      ))}
    </nav>
  );
}

function Backoffice({ session, sections }: { session: StaffSession; sections: typeof SECTIONS }) {
  const [section, setSection] = useState<Section>(() => sections[0]?.id ?? 'arena');
  // Saltar de uma ficha de socio para o streamer (e vice-versa): a seccao abre
  // nessa conta. `key` remonta-a, para abrir de novo mesmo que ja la estivesse.
  const [jump, setJump] = useState<{ accountId: string; n: number } | null>(null);
  const jumpTo = (target: Section) => (accountId: string) => {
    setJump((j) => ({ accountId, n: (j?.n ?? 0) + 1 }));
    setSection(target);
  };
  // Do separador Conteudo de um evento para o plano em Redes sociais.
  const [socialPlan, setSocialPlan] = useState<{ planId: string; n: number } | null>(null);
  const choose = (target: Section) => {
    setJump(null);
    setSocialPlan(null);
    setSection(target);
  };

  return (
    <Theme name="admin" className="min-h-screen">
      {/* Fixo no topo: as secoes ficam sempre a mao em listas compridas. */}
      <header className="sticky top-0 z-40 border-b border-gu-line bg-gu-raised">
        <Container width="full" className="flex flex-wrap items-center gap-x-6 gap-y-2 py-3">
          <h1 className="flex items-center gap-3">
            <Logo height={18} />
            <span className="font-sub text-label font-bold uppercase text-gu-ink-muted">
              Gestão
            </span>
          </h1>
          {/* Em ecras pequenos fica numa linha propria e desliza para o lado, em vez
              de alargar a pagina. */}
          <nav
            className="order-last flex w-full gap-1 overflow-x-auto [scrollbar-width:none] lg:order-none lg:w-auto"
            aria-label="Secções"
          >
            {sections.map((s) => (
              <button
                key={s.id}
                type="button"
                aria-current={section === s.id ? 'page' : undefined}
                onClick={() => choose(s.id)}
                className={cx(
                  'shrink-0 whitespace-nowrap border-b-2 px-3 py-2 font-sub text-label font-bold uppercase',
                  section === s.id
                    ? 'border-gu-accent text-gu-ink'
                    : 'border-transparent text-gu-ink-muted hover:text-gu-ink',
                )}
              >
                {s.label}
              </button>
            ))}
          </nav>
          <div className="ml-auto flex items-center gap-4">
            <SiteLinks />
            <Button variant="ghost" size="sm" onClick={() => void signOut()}>
              {session.login} · Sair
            </Button>
          </div>
        </Container>
      </header>
      <main>
        <Container width="full" className="flex flex-col gap-4 py-6">
          {section === 'arena' ? <ArenaSection session={session} /> : null}
          {section === 'socios' ? (
            <MembersSection
              key={jump?.n ?? 0}
              session={session}
              openAccountId={jump?.accountId ?? null}
              onOpenStreamer={canManageStreaming(session) ? jumpTo('streaming') : undefined}
            />
          ) : null}
          {section === 'loja' ? <StoreSection /> : null}
          {section === 'eventos' ? (
            <EventsSection
              canBudget={canManageEventBudget(session)}
              me={session.accountId}
              onOpenSocialPlan={
                hasSocialAccess(session)
                  ? (planId) => {
                      setSocialPlan((j) => ({ planId, n: (j?.n ?? 0) + 1 }));
                      setSection('redes');
                    }
                  : undefined
              }
            />
          ) : null}
          {section === 'equipas' ? <TeamsSection /> : null}
          {section === 'torneios' ? <TournamentsSection /> : null}
          {section === 'passatempos' ? <GiveawaysSection /> : null}
          {section === 'conteudos' ? <ContentSection session={session} /> : null}
          {section === 'redes' ? (
            <SocialSection
              key={socialPlan?.n ?? 0}
              canManage={canManageSocial(session)}
              me={session.accountId}
              initialPlan={socialPlan?.planId ?? null}
            />
          ) : null}
          {section === 'streaming' ? (
            <StreamingSection
              key={jump?.n ?? 0}
              manage={canManageStreaming(session)}
              padrinho={isStreamingPadrinho(session)}
              channel={canProgramChannel(session)}
              openAccountId={jump?.accountId ?? null}
              onOpenMember={canViewMembers(session) ? jumpTo('socios') : undefined}
            />
          ) : null}
          {section === 'contactos' ? (
            <ContactsSection
              general={canManageContacts(session)}
              direction={canReadDirectionContacts(session)}
            />
          ) : null}
          {section === 'staff' ? <StaffView selfId={session.accountId} /> : null}
        </Container>
      </main>
    </Theme>
  );
}

function ArenaSection({ session }: { session: StaffSession }) {
  const [arenas, setArenas] = useState<Arena[] | null>(null);
  const [arenaId, setArenaId] = useState<string | null>(null);
  const [tab, setTab] = useState<Tab>('sala');
  const [error, setError] = useState<string | null>(null);
  const admin = isAdmin(session);

  const loadArenas = useCallback(() => {
    getJson('/api/arena/arenas', z.array(arenaSchema))
      .then((list) => {
        setArenas(list);
        setArenaId((current) => current ?? list[0]?.id ?? null);
      })
      .catch((e: unknown) => setError(messageOf(e)));
  }, []);

  useEffect(loadArenas, [loadArenas]);

  const arena = arenas?.find((a) => a.id === arenaId) ?? null;

  return (
    <>
      <div className="flex flex-wrap items-center gap-3">
        {/* No separador Arenas nao ha arena escolhida: a lista mostra todas. */}
        {tab === 'arenas' ? (
          <span className="font-display text-title uppercase">Todas as arenas</span>
        ) : arenas && arenas.length > 1 ? (
          <Select
            aria-label="Arena"
            className="max-w-xs"
            value={arenaId ?? ''}
            onChange={(e) => setArenaId(e.target.value)}
          >
            {arenas.map((a) => (
              <option key={a.id} value={a.id}>
                {a.name}
              </option>
            ))}
          </Select>
        ) : arena ? (
          <span className="font-display text-title uppercase">{arena.name}</span>
        ) : null}
        <nav
          className="flex max-w-full gap-1 overflow-x-auto [scrollbar-width:none]"
          aria-label="Arena"
        >
          {TABS.filter((t) => !t.adminOnly || admin).map((t) => (
            <button
              key={t.id}
              type="button"
              aria-current={tab === t.id ? 'page' : undefined}
              onClick={() => setTab(t.id)}
              className={cx(
                'shrink-0 whitespace-nowrap border-b-2 px-3 py-2 font-sub text-label font-bold uppercase',
                tab === t.id
                  ? 'border-gu-accent text-gu-ink'
                  : 'border-transparent text-gu-ink-muted hover:text-gu-ink',
              )}
            >
              {t.label}
            </button>
          ))}
        </nav>
      </div>
      {error ? <Notice tone="danger">{error}</Notice> : null}
      {arenas && arenas.length === 0 && !admin ? (
        <Notice tone="info">Não tem nenhuma arena atribuída. Peça a um administrador.</Notice>
      ) : null}
      {arena && tab === 'config' && admin ? (
        <ArenaConfigView key={arena.id} arenaId={arena.id} />
      ) : null}
      {!arena && tab === 'config' && admin && arenas ? (
        <Notice tone="info">Ainda não há arenas. Crie a primeira no separador Arenas.</Notice>
      ) : null}
      {tab === 'arenas' && admin ? (
        <ArenasView
          arenas={arenas ?? []}
          onConfigure={(id) => {
            setArenaId(id);
            setTab('config');
          }}
          onCreated={(created) => {
            setArenaId(created.id);
            loadArenas();
          }}
        />
      ) : null}
      {arena && tab === 'sala' ? <RoomView arenaId={arena.id} /> : null}
      {arena && tab === 'jovens' ? (
        <YouthsView arenaId={arena.id} canRegister={canRegister(session, arena.id)} />
      ) : null}
      {arena && tab === 'sessoes' ? <SessionsView arenaId={arena.id} /> : null}
      {arena && tab === 'ocupacao' ? <OccupancyView arenaId={arena.id} /> : null}
    </>
  );
}
